import { createSignal, createEffect } from 'solid-js';
import { PaperPlaneRightIcon, CloseIcon } from '../Icons';
import classNames from 'classnames';

interface FeedbackReasonProps {
  open: boolean;
  onSubmit?: (reason: string) => void;
  placeholder?: string;
  maxLength?: number;
}

export function FeedbackReason({
  open,
  onSubmit,
  placeholder = 'What could we have done better? Your feedback is valuable to us...',
  maxLength = 500,
}: FeedbackReasonProps) {
  const [reason, setReason] = createSignal('');
  let textareaRef: HTMLTextAreaElement | undefined;

  createEffect(() => {
    if (open) {
      setReason('');
      setTimeout(() => textareaRef?.focus(), 0);
    }
  });

  const handleSubmit = () => {
    if (reason().trim()) {
      onSubmit?.(reason());
    }
  };

  const handleCancel = () => {
    setReason('');
    textareaRef?.focus();
  };

  const buttonClass =
    'w-9 h-9 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0';
  const ghostVariant = 'hover:bg-accent hover:text-accent-foreground';
  const defaultVariant =
    'bg-primary text-primary-foreground shadow hover:bg-primary/90';

  return (
    <div class="mt-2 w-full">
      <div
        data-slot="card"
        class="bg-card text-card-foreground flex flex-col gap-2 rounded-lg border p-3 shadow-none focus-within:ring-2"
      >
        <div data-slot="card-content" class="px-0">
          <textarea
            ref={(el) => (textareaRef = el)}
            class="w-full border-none shadow-none focus-visible:ring-0 p-0 focus-visible:outline-none"
            placeholder={placeholder}
            maxlength={maxLength}
            value={reason()}
            rows={4}
            onInput={(e) => setReason(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <div class="flex justify-end mt-1">
            <span class="text-xs text-muted-foreground select-none">
              {maxLength - reason().length} characters remaining
            </span>
          </div>
        </div>
        <div
          data-slot="card-footer"
          class="flex items-center [.border-t]:pt-6 p-0 gap-2 flex-row-reverse"
        >
          <button
            type="button"
            aria-label="Submit feedback"
            disabled={!reason().length}
            onClick={handleSubmit}
            class={`text-[var(--bfsearch-input-button-color)] bg-[var(--bfsearch-input-button-bg-color)] ${classNames(
              buttonClass,
              defaultVariant,
            )}`}
          >
            <PaperPlaneRightIcon aria-hidden="true" />
          </button>
          <button
            aria-label="Cancel feedback"
            class={classNames(buttonClass, ghostVariant)}
            type="button"
            onClick={handleCancel}
          >
            <CloseIcon aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
